import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Sparkles, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { AIDashboardViewProps } from './aidashboard.props';

const AIDashboardView: React.FC<AIDashboardViewProps> = ({ insights, aiMetrics, generateNewInsight }) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <Sparkles className="w-8 h-8" />
                <h2 className="text-3xl font-bold">AI Dashboard</h2>
              </div>
              <p className="text-blue-100 text-lg">
                Интелигентни анализи и препораки за подобрување на вашите настани
              </p>
            </div>
            <Button
              onClick={generateNewInsight}
              className="bg-white text-blue-600 hover:bg-blue-50"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Генерирај нови увиди
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AI Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {aiMetrics.map((metric, index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <metric.icon className="w-8 h-8 text-primary" />
                <Brain className="w-5 h-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground mb-2">{metric.label}</p>
              <p className="text-2xl font-bold text-foreground mb-1">{metric.value}</p>
              <p className="text-xs text-green-600">{metric.change}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AI Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Brain className="w-6 h-6 mr-2 text-purple-600" />
            AI Увиди и Препораки
          </CardTitle>
          <CardDescription>
            Персонализирани препораки врз основа на анализа на податоци
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {insights.map((insight, index) => (
              <div
                key={index}
                className={`p-6 rounded-lg border-l-4 ${
                  insight.priority === 'high' ? 'border-red-500 bg-red-50' : 'border-blue-500 bg-blue-50'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`${insight.color} p-3 rounded-lg`}>
                    <insight.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-foreground">{insight.title}</h4>
                      <span
                        className={`px-3 py-1 text-xs rounded-full ${
                          insight.priority === 'high'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {insight.priority === 'high' ? 'Висок приоритет' : 'Среден приоритет'}
                      </span>
                    </div>
                    <p className="text-muted-foreground">{insight.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Predictive Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Предвидување на трендови</CardTitle>
            <CardDescription>Проекции за следните 6 месеци</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Очекувани регистрации</span>
                  <span className="text-sm font-bold text-green-600">+28%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Задоволство на корисници</span>
                  <span className="text-sm font-bold text-primary">92%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Стапка на повторување</span>
                  <span className="text-sm font-bold text-purple-600">65%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-purple-500 h-2 rounded-full" style={{ width: '65%' }}></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Препораки за оптимизација</CardTitle>
            <CardDescription>Акции за подобрување на перформансите</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-foreground">Оптимизирајте цени</p>
                  <p className="text-sm text-muted-foreground">Намалување на цената за 10% може да зголеми регистрациите за 23%</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-primary/10 rounded-lg">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-foreground">Email кампањи</p>
                  <p className="text-sm text-muted-foreground">Испраќајте потсетници 7, 3 и 1 ден пред настанот</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-foreground">Социјални медиуми</p>
                  <p className="text-sm text-muted-foreground">Најдобро време за објавување: 18:00-20:00 часот</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIDashboardView;
